import { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { PortalSpinner } from 'components/PortalSpinner';
import Stepper from 'components/Profile/Stepper';
import { ProfileActions } from 'store/actions';
import { PORTAL_ROUTES } from 'utils/constants';
import {
  getProfileCompletion,
  getStepById,
  getStepIndex,
  getNextStep,
  canOpenStep,
  getFirstIncompleteStep,
  stepRoute,
} from './helper';

const enhancer = connect(null, { ...ProfileActions });

const SECTION_KEY = {
  personal: 'personal_information',
  insurance: 'insurance_intake',
  medical: 'medical_history',
  hipaa: 'hipaa_forms',
  consent: 'consent_forms',
  treatment: 'upcoming_treatments_consent_forms',
};

const ProfileLayout = ({ showProfile }) => {
  const [profile, setProfile] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const lastSegment = location.pathname.split('/').filter(Boolean).pop();
  const refetchOnEnter = ['hipaa', 'consent'].includes(lastSegment);

  useEffect(() => {
    if (refetchOnEnter || !profile) fetchProfile();
  }, [lastSegment]);

  async function fetchProfile() {
    try {
      const { data } = await showProfile();
      setProfile(data);
    } catch (error: any) {
      toast.error(error.errors || 'Failed to load profile. Please try again later.');
    }
  }

  const mergeSection = (id, resource) =>
    setProfile((p) => ({ ...p, attributes: { ...p.attributes, [SECTION_KEY[id]]: { data: resource } } }));

  const attributes = profile?.attributes;
  const completion = getProfileCompletion(attributes);

  const sectionData = {
    personal: attributes?.personal_information?.data?.attributes,
    insurance: attributes?.insurance_intake?.data?.attributes,
    medical: attributes?.medical_history?.data?.attributes,
    hipaa: attributes?.hipaa_forms?.data,
    consent: attributes?.consent_forms?.data,
    treatmentConsents: attributes?.upcoming_treatments_consent_forms?.data,
    contacts: {
      authorized: attributes?.authorized_contacts?.data,
      revoked: attributes?.revoked_contacts?.data,
      family_members: attributes?.family_members?.data,
      emergency_contacts: attributes?.emergency_contacts?.data,
    },
    practice: attributes?.practice?.data?.attributes
  };

  const activeStep = getStepById(lastSegment);
  const lockedActive = activeStep && !canOpenStep(getStepIndex(activeStep.id), completion);

  const goToStep = (step) => navigate(stepRoute(step));

  const goToNextStep = (currentId) => {
    const next = getNextStep(currentId);
    navigate(next ? stepRoute(next) : PORTAL_ROUTES.PROFILE);
  };

  if (!profile) return <PortalSpinner />;

  return (
    <div className="relative -m-5 flex-1 lg:-m-6">
      <div className="absolute inset-0 flex flex-col overflow-hidden">
        {activeStep && (
          <Stepper completion={completion} activeId={activeStep.id} onStepClick={goToStep} />
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {lockedActive ? (
            <Navigate to={stepRoute(getFirstIncompleteStep(completion))} replace />
          ) : (
            <Outlet context={{ sectionData, completion, mergeSection, goToNextStep, refreshProfile: fetchProfile }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default enhancer(ProfileLayout);
